import { useGetTeacherLoadQuery } from '@/store/api';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

/*
  ═════════════════════════════════════════════════════════════════════════════
  WHO IS CARRYING HOW MUCH

  The clash check catches a teacher being in two rooms at once. It cannot
  catch the other way a timetable goes wrong: one teacher on thirty periods
  while a colleague is on four. Every single one of those slots is legal, so
  nothing refuses it, and the person it lands on finds out by being exhausted
  in week three.

  It is a flat list, heaviest first, because the point is the outlier at each
  end — and it is the office's screen, not a teacher's: somebody shown only
  their own row learns nothing about whether the load is fair.

  Deliberately no "recommended maximum". That is a staffing judgement that
  belongs to the head teacher and varies by school, subject and contract; a
  number invented here would be read as a rule.
  ═════════════════════════════════════════════════════════════════════════════
*/
export function TeacherLoadDialog({
  open,
  onClose,
  institutionId,
}: {
  open: boolean;
  onClose: () => void;
  institutionId: string;
}) {
  const { data = [], isLoading } = useGetTeacherLoadQuery(institutionId, {
    skip: !institutionId || !open,
  });

  const busiest = data[0]?.periods ?? 0;
  const total = data.reduce((n, t) => n + t.periods, 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Teaching load"
      description="Periods a week, across every class"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : data.length === 0 ? (
        <p className="py-6 text-center text-[11.5px] text-muted-foreground">
          No teachers on the staff list yet.
        </p>
      ) : (
        <div className="flex flex-col gap-2.5">
          <p className={styles.meta}>
            {total} timetabled period{total === 1 ? '' : 's'} shared between {data.length} teacher
            {data.length === 1 ? '' : 's'}.
          </p>

          <div className="flex flex-col divide-y divide-border rounded-lg border border-border">
            {data.map((t) => (
              <div key={t.teacherId} className="flex items-center gap-3 px-3 py-2">
                <span className="min-w-0 flex-1 truncate text-[11.5px] font-semibold text-foreground">
                  {t.fullName}
                </span>

                {/* Bars relative to the BUSIEST person, not to a fixed ceiling:
                    the comparison that matters here is between colleagues. */}
                <span className="h-1.5 w-24 overflow-hidden rounded-full bg-muted">
                  <span
                    className="block h-full rounded-full bg-primary"
                    style={{ width: busiest === 0 ? '0%' : `${(t.periods / busiest) * 100}%` }}
                  />
                </span>

                <span className="w-[86px] text-right">
                  <span className="text-[11.5px] font-bold tabular-nums text-foreground">
                    {t.periods}
                  </span>
                  <span className={cn(styles.meta, 'ml-1')}>
                    period{t.periods === 1 ? '' : 's'}
                  </span>
                </span>

                <span className={cn(styles.meta, 'w-[68px] text-right tabular-nums')}>
                  {t.classes} class{t.classes === 1 ? '' : 'es'}
                </span>
              </div>
            ))}
          </div>

          {data.some((t) => t.periods === 0) && (
            <p className={styles.meta}>
              A teacher on none is either newly added or not yet timetabled — both worth checking
              before term starts.
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
